import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ESTIMATE_MINUTES_OPTIONS, formatEstimateLabel } from '../../utils/estimateTime';
import { getWorkItemTypeOptions } from '../../utils/reactionConfig';
import { fetchOrgWorkflowReactions } from '../../store/slices/workflowCatalogSlice';
import { fetchMessageTemplates } from '../../services/messageTemplateService';

export const FOLLOW_UP_TYPES = [
  { value: 'TASK', label: 'Create a task' },
  { value: 'MESSAGE', label: 'Post a message' },
  { value: 'WORKFLOW', label: 'Create a workflow' },
  { value: 'ANNOUNCEMENT', label: 'Create an announcement' },
  { value: 'TEMPLATE', label: 'Trigger a template' },
];

const TYPE_TONE = {
  TASK: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:border-sky-800',
  MESSAGE: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-800',
  WORKFLOW: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800',
  ANNOUNCEMENT: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800',
  TEMPLATE: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600',
};

export const emptyFollowUp = (actionType = 'MESSAGE') => ({
  id: `fu_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  actionType,
  title: '',
  body: '',
  mentionUserIds: [],
  templateId: '',
  workflowReactionId: '',
  estimateTimeInMinutes: 30,
  messageTarget: 'REPLY',
  targetChannelId: '',
  targetChannelIds: [],
});

export const cloneFollowUps = (actions = []) => (actions || []).map((action) => ({
  id: action.id || `fu_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  actionType: action.actionType || 'MESSAGE',
  title: action.title || '',
  body: action.body || '',
  mentionUserIds: [...(action.mentionUserIds || [])],
  templateId: action.templateId || '',
  workflowReactionId: action.workflowReactionId || '',
  estimateTimeInMinutes: action.estimateTimeInMinutes || 30,
  messageTarget: ['CHANNEL', 'SELF', 'USER', 'PERSONAL'].includes(action.messageTarget)
    ? (action.messageTarget === 'PERSONAL' ? 'USER' : action.messageTarget)
    : 'REPLY',
  targetChannelId: action.targetChannelId || '',
  targetChannelIds: Array.isArray(action.targetChannelIds) && action.targetChannelIds.length
    ? [...action.targetChannelIds]
    : (action.targetChannelId ? [action.targetChannelId] : []),
}));

const personLabel = (user) => (
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || 'User'
);

const fieldClass = 'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400';

export const UserCheckboxList = ({
  users = [],
  selectedIds = [],
  disabled = false,
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const filtered = (users || []).filter((user) => {
    if (!needle) return true;
    const hay = `${personLabel(user)} ${user?.username || ''} ${user?.email || ''}`.toLowerCase();
    return hay.includes(needle);
  });

  return (
    <div className="space-y-1.5">
      <input
        type="search"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search people"
        className={fieldClass}
      />
      <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1.5 space-y-0.5">
        {filtered.map((user) => {
          const checked = selectedIds.includes(user.id);
          return (
            <label
              key={user.id}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer ${
                checked
                  ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-100'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => {
                  const next = new Set(selectedIds);
                  if (next.has(user.id)) next.delete(user.id);
                  else next.add(user.id);
                  onChange?.([...next]);
                }}
              />
              <span className="truncate">{personLabel(user)}</span>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-xs text-gray-500 px-2 py-2">No users found.</p>
        )}
      </div>
    </div>
  );
};

const selectedChannelIds = (action) => {
  if (Array.isArray(action?.targetChannelIds) && action.targetChannelIds.length) {
    return action.targetChannelIds;
  }
  return action?.targetChannelId ? [action.targetChannelId] : [];
};

const isDirectChannel = (channel) => {
  const type = String(channel?.type || '').toUpperCase();
  return type === 'DIRECT_MESSAGE' || type === 'DIRECT' || type === 'DM';
};

export const ChannelCheckboxList = ({
  channels = [],
  selectedIds = [],
  disabled = false,
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const channelOnly = (channels || []).filter((channel) => channel && !channel.systemChannelKey && !isDirectChannel(channel));
  const filtered = channelOnly.filter((channel) => {
    if (!needle) return true;
    return `${channel?.name || ''}`.toLowerCase().includes(needle);
  });

  return (
    <div className="space-y-1.5">
      <input
        type="search"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search channels"
        className={fieldClass}
      />
      <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1.5 space-y-0.5">
        {filtered.map((channel) => {
          const checked = selectedIds.includes(channel.id);
          return (
            <label
              key={channel.id}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer ${
                checked
                  ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-100'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => {
                  const next = new Set(selectedIds);
                  if (next.has(channel.id)) next.delete(channel.id);
                  else next.add(channel.id);
                  onChange?.([...next]);
                }}
              />
              <span className="truncate">{channel.name || 'Untitled channel'}</span>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-xs text-gray-500 px-2 py-2">No channels found.</p>
        )}
      </div>
    </div>
  );
};

const destinationId = (action) => {
  if (action?.messageTarget === 'PERSONAL' || action?.messageTarget === 'USER') return 'USER';
  if (action?.messageTarget === 'SELF') return 'SELF';
  return 'CHANNEL';
};

const destinationLabel = (action, channelName) => {
  const target = destinationId(action);
  if (target === 'SELF') return 'Self';
  if (target === 'USER') {
    const count = (action?.mentionUserIds || []).length;
    return count ? `${count} ${count === 1 ? 'person' : 'people'}` : 'Specific user';
  }
  const ids = selectedChannelIds(action);
  if (ids.length) return `${ids.length} ${ids.length === 1 ? 'channel' : 'channels'}`;
  return channelName || 'Same channel';
};

const typeLabel = (actionType) => (
  FOLLOW_UP_TYPES.find((type) => type.value === actionType)?.label || 'Follow-up'
);

const Chevron = ({ open }) => (
  <svg
    className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const PostCompletionEditor = ({
  enabled,
  actions = [],
  onChange,
  users = [],
  excludeTemplateId = '',
  title = 'Post completion',
  description = 'When this is marked complete, also post a message, announcement, or the other follow-ups below.',
  disabled = false,
  channelName = '',
  channels = [],
}) => {
  const dispatch = useDispatch();
  const workflowCatalogItems = useSelector((state) => state.workflowCatalog?.items || []);
  const workflowTypeOptions = useMemo(
    () => getWorkItemTypeOptions(workflowCatalogItems),
    [workflowCatalogItems],
  );
  const [templates, setTemplates] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const seenIds = useRef(new Set((actions || []).map((action) => action.id).filter(Boolean)));

  useEffect(() => {
    dispatch(fetchOrgWorkflowReactions());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    fetchMessageTemplates()
      .then((list) => {
        if (!cancelled) setTemplates(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ids = (actions || []).map((action) => action.id).filter(Boolean);
    const fresh = ids.filter((id) => !seenIds.current.has(id));
    fresh.forEach((id) => seenIds.current.add(id));
    if (!fresh.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      fresh.forEach((id) => next.add(id));
      return next;
    });
  }, [actions]);

  const updateActions = (next) => {
    onChange?.({ enabled: true, actions: next });
  };

  const patchAction = (index, patch) => {
    const next = [...(actions || [])];
    next[index] = { ...next[index], ...patch };
    updateActions(next);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const actionIds = useMemo(
    () => (actions || []).map((action) => action.id).filter(Boolean),
    [actions],
  );
  const allExpanded = actionIds.length > 0 && actionIds.every((id) => expandedIds.has(id));

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 overflow-hidden">
      <div className="flex items-start gap-3 px-4 py-3 bg-gray-50/80 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <label className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(enabled)}
            disabled={disabled}
            onChange={(e) => {
              const turningOn = e.target.checked;
              const nextActions = turningOn && !(actions || []).length
                ? [emptyFollowUp('MESSAGE')]
                : actions;
              if (turningOn && nextActions[0]?.id) {
                setExpandedIds((prev) => new Set(prev).add(nextActions[0].id));
              }
              onChange?.({ enabled: turningOn, actions: nextActions });
            }}
          />
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-900 dark:text-white">{title}</span>
              {enabled && (
                <span className="text-[11px] rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200 px-2 py-0.5">
                  {(actions || []).length} {(actions || []).length === 1 ? 'item' : 'items'}
                </span>
              )}
            </span>
            <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{description}</span>
          </span>
        </label>
        {enabled && (actions || []).length > 0 && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setExpandedIds(allExpanded ? new Set() : new Set(actionIds))}
            className="text-[11px] font-medium text-primary-600 dark:text-primary-300 hover:underline flex-shrink-0"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      {enabled && (
        <div className="p-3 space-y-2.5">
          {(actions || []).map((action, index) => {
            const open = expandedIds.has(action.id);
            const tone = TYPE_TONE[action.actionType] || TYPE_TONE.TEMPLATE;
            return (
              <div
                key={action.id || index}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 overflow-hidden"
              >
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleExpanded(action.id)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    aria-expanded={open}
                  >
                    <span className="text-gray-400"><Chevron open={open} /></span>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}>
                      {typeLabel(action.actionType)}
                    </span>
                    <span className="text-sm text-gray-900 dark:text-white truncate">
                      {action.actionType === 'TEMPLATE'
                        ? (templates.find((t) => t.id === action.templateId)?.name || 'Choose a template')
                        : (action.title || 'Untitled')}
                    </span>
                    <span className="hidden sm:inline text-[11px] text-gray-400 truncate">
                      {destinationLabel(action, channelName)}
                      {action.actionType === 'TASK' && action.estimateTimeInMinutes
                        ? ` · ${formatEstimateLabel(action.estimateTimeInMinutes)}`
                        : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => updateActions((actions || []).filter((item) => item.id !== action.id))}
                    className="text-[11px] text-rose-500 hover:underline flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>

                {open && (
                  <div className="px-3 pb-3 space-y-3 border-t border-gray-200/80 dark:border-gray-700">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3">
                      <label className="block">
                        <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Type</span>
                        <select
                          value={action.actionType}
                          disabled={disabled}
                          onChange={(e) => {
                            const nextType = e.target.value;
                            patchAction(index, {
                              actionType: nextType,
                              workflowReactionId: nextType === 'WORKFLOW' ? action.workflowReactionId : '',
                              templateId: nextType === 'TEMPLATE' ? action.templateId : '',
                            });
                          }}
                          className={fieldClass}
                        >
                          {FOLLOW_UP_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </label>

                      {action.actionType === 'TEMPLATE' ? (
                        <label className="block">
                          <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Template</span>
                          <select
                            value={action.templateId || ''}
                            disabled={disabled}
                            onChange={(e) => patchAction(index, { templateId: e.target.value })}
                            className={fieldClass}
                          >
                            <option value="">Select an existing template…</option>
                            {templates
                              .filter((t) => t.active && t.id !== excludeTemplateId)
                              .map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name} · {t.templateType}
                                </option>
                              ))}
                          </select>
                        </label>
                      ) : (
                        <label className="block">
                          <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {action.actionType === 'MESSAGE' ? 'Title (optional)' : 'Title'}
                          </span>
                          <input
                            value={action.title || ''}
                            disabled={disabled}
                            onChange={(e) => patchAction(index, { title: e.target.value })}
                            placeholder={action.actionType === 'MESSAGE' ? 'Optional title' : 'What should this be called?'}
                            className={fieldClass}
                          />
                        </label>
                      )}

                      {action.actionType === 'TASK' && (
                        <label className="block">
                          <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Estimated time</span>
                          <select
                            value={String(action.estimateTimeInMinutes || 30)}
                            disabled={disabled}
                            onChange={(e) => patchAction(index, { estimateTimeInMinutes: parseInt(e.target.value, 10) })}
                            className={fieldClass}
                          >
                            {ESTIMATE_MINUTES_OPTIONS.map((m) => (
                              <option key={m} value={String(m)}>{formatEstimateLabel(m)}</option>
                            ))}
                          </select>
                        </label>
                      )}

                      {action.actionType === 'WORKFLOW' && (
                        <label className="block sm:col-span-2">
                          <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Workflow type</span>
                          <select
                            value={action.workflowReactionId || ''}
                            disabled={disabled}
                            onChange={(e) => patchAction(index, { workflowReactionId: e.target.value })}
                            className={fieldClass}
                          >
                            <option value="">Choose a workflow type…</option>
                            {workflowTypeOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>{opt.emoji} {opt.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>

                    <label className="block">
                      <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {action.actionType === 'MESSAGE' ? 'Message' : 'Details'}
                      </span>
                      <textarea
                        value={action.body || ''}
                        disabled={disabled}
                        onChange={(e) => patchAction(index, { body: e.target.value })}
                        rows={2}
                        placeholder={action.actionType === 'MESSAGE' ? 'What should be posted?' : 'Optional details'}
                        className={fieldClass}
                      />
                    </label>

                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Where it goes</p>
                        <span className="text-[11px] text-gray-400">{destinationLabel(action, channelName)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { id: 'SELF', label: 'Self' },
                          { id: 'CHANNEL', label: 'Post on channel' },
                          { id: 'USER', label: 'Post in specific user' },
                        ].map((option) => {
                          const active = destinationId(action) === option.id;
                          return (
                            <button
                              key={option.id}
                              type="button"
                              disabled={disabled}
                              onClick={() => patchAction(index, { messageTarget: option.id })}
                              className={`text-[11px] rounded-full border px-2.5 py-1 ${
                                active
                                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      {destinationId(action) === 'SELF' ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">Posts in your own chat.</p>
                      ) : destinationId(action) === 'CHANNEL' ? (
                        (channels || []).length > 0 ? (
                          <ChannelCheckboxList
                            channels={channels}
                            selectedIds={selectedChannelIds(action)}
                            disabled={disabled}
                            onChange={(ids) => patchAction(index, {
                              messageTarget: 'CHANNEL',
                              targetChannelIds: ids,
                              targetChannelId: ids[0] || '',
                            })}
                          />
                        ) : channelName ? (
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            Channel: <span className="font-medium text-gray-900 dark:text-white">{channelName}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Posts on the same channel.</p>
                        )
                      ) : (
                        <UserCheckboxList
                          users={users}
                          selectedIds={action.mentionUserIds || []}
                          disabled={disabled}
                          onChange={(ids) => patchAction(index, { mentionUserIds: ids })}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {(actions || []).length === 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
              No follow-ups yet. Add one below.
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {FOLLOW_UP_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                disabled={disabled}
                onClick={() => {
                  const next = emptyFollowUp(type.value);
                  setExpandedIds((prev) => new Set(prev).add(next.id));
                  seenIds.current.add(next.id);
                  updateActions([...(actions || []), next]);
                }}
                className="text-xs rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-2.5 py-1.5 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 hover:border-primary-400"
              >
                + {type.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostCompletionEditor;
